import { getInlineNodeRange, getInlineRange, getTextRanges, getWordRange } from '@common/prosemirror/model/range';
import { getTrackedChangesState, TrackedChangesIgnoreMetaKey, TrackedChangesTrackedMetaKey } from '@common/prosemirror/plugins/tracked-changes.plugin';
import { ChangeType } from '@common/prosemirror/tracked-changes/change-type.enum';
import { Attrs, NodeRange, NodeType } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';
import { findWrapping } from 'prosemirror-transform';
import { EditorView } from 'prosemirror-view';

export interface WrapInlineNodeOptions {
  canWrapEmptySelection?: boolean;
  limitRangeToOneNode?: boolean;
  skipTrackedChanges?: boolean;
  wrapParentForEmptySelection?: boolean;
  wrapWordForEmptySelection?: boolean;
}

export type WrapInlineNodeCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Dictionary) => boolean;

export function wrapInInlineNode(nodeType: NodeType, options: WrapInlineNodeOptions = {}): WrapInlineNodeCommand {
  return function (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, attrs?: Dictionary): boolean {
    if (state.selection.empty && !options.wrapParentForEmptySelection && !options.wrapWordForEmptySelection && !options.canWrapEmptySelection) {
      return false;
    }

    // Calculate the ranges to wrap
    let ranges: NodeRange[];
    let range: NodeRange;

    // If the selection is empty
    if (state.selection.empty) {
      // If the parent should be wrapped
      if (options.wrapParentForEmptySelection) {
        // Grab the range for the inline node that the cursor is in
        range = getInlineNodeRange(state.selection.$from);
        if (range) {
          ranges = [range];
        }
      } else if (options.wrapWordForEmptySelection) { // Else if the word should be wrapped
        // Grab the range for the word in the text node that the cursor is in
        range = getWordRange(state.selection.$from);
        if (range) {
          ranges = [range];
        }
      }
      // Else there is a selection so use it for the range
    } else {
      if (options.limitRangeToOneNode) {
        range = getInlineRange(state.selection.$from, state.selection.$to);
        if (range) {
          ranges = [range];
        }
      } else {
        ranges = getTextRanges(state.selection.from, state.selection.to, state.doc);
      }
    }

    // If there are no valid ranges
    if (!Array.isArray(ranges) || ranges.length === 0) {
      return false;
    }

    const wrappings: { type: NodeType; attrs: Attrs | null; }[][] = [];
    for (let i = 0; i < ranges.length; i += 1) {
      // findWrapping requires the range's parent to have children
      if (ranges[i].parent.childCount > 0) {
        wrappings.push(findWrapping(ranges[i], nodeType));
      } else {
        wrappings.push(null);
      }
    }

    // If there are no valid wrappings
    if (!wrappings.some(wrap => !!wrap)) {
      return false;
    }

    // If the transaction should be dispatched
    if (dispatch) {
      // Create the transaction
      const tr = state.tr;
      const trackedChangesState = getTrackedChangesState(state);

      // Apply the wraps in reverse document order. Why? I'm not sure but it fails otherwise. Maybe something to do with position changes?
      for (let i = ranges.length - 1; i >= 0; i -= 1) {
        const wrapping = wrappings[i];
        // If there is a valid wrapping for this range
        if (wrapping) {
          wrapping.forEach((wrap => {
            wrap.attrs = {
              ...wrap.attrs,
              ...attrs,
              ...trackedChangesState?.enabled && !options.skipTrackedChanges ? trackedChangesState.makeTrackedChangeNodeAttrs(ChangeType.Bind, { changeId: trackedChangesState.getParentChangeId(ranges[i].$from, ChangeType.Bind) }) : {}
            };
          }))
          tr.wrap(ranges[i], wrappings[i]);
        }
      }

      // Mark that the changes were tracked
      if (trackedChangesState?.enabled) {
        if (options.skipTrackedChanges) {
          tr.setMeta(TrackedChangesIgnoreMetaKey, true);
        } else {
          tr.setMeta(TrackedChangesTrackedMetaKey, true);
        }
      }

      // Dispatch the transaction
      dispatch(tr.scrollIntoView());
    }

    // Return true indicating the command can/did execute
    return true;
  };
}
